import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon, Video, FileText, MessageSquareQuote, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KINDS = [
  { value: "photo",       label: "Product photo", icon: ImageIcon },
  { value: "video",       label: "Promo video",   icon: Video },
  { value: "testimonial", label: "Testimonial",   icon: MessageSquareQuote },
  { value: "brochure",    label: "Brochure (PDF)", icon: FileText },
];

const ACCEPT_BY_KIND = {
  photo:       "image/jpeg,image/png,image/webp,image/gif",
  video:       "video/mp4,video/webm,video/quicktime",
  testimonial: "image/jpeg,image/png,image/webp,video/mp4,video/webm",
  brochure:    "application/pdf",
};

const MAX_MB = 50;

function MediaCard({ item, onDelete }) {
  const fullUrl = item.url?.startsWith("http") ? item.url : `${BACKEND}${item.url}`;
  const isImage = item.mime?.startsWith("image/");
  const isVideo = item.mime?.startsWith("video/");
  const isPDF   = item.mime === "application/pdf";

  return (
    <div className="border border-zinc-200 bg-white overflow-hidden flex flex-col" data-testid={`media-card-${item.media_id}`}>
      <div className="aspect-video bg-zinc-100 grid place-items-center overflow-hidden relative">
        {isImage && <img src={fullUrl} alt={item.title || item.filename} className="w-full h-full object-cover" />}
        {isVideo && <video src={fullUrl} className="w-full h-full object-cover" controls preload="metadata" />}
        {isPDF && (
          <a href={fullUrl} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 text-zinc-700 hover:text-[#C8102E]">
            <FileText className="w-12 h-12" />
            <span className="text-xs font-semibold">View PDF</span>
          </a>
        )}
      </div>
      <div className="p-3 flex-grow">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[#C8102E]">{item.kind}</div>
        <div className="font-semibold text-sm mt-1 truncate" title={item.title || item.filename}>{item.title || item.filename}</div>
        {item.description && <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{item.description}</div>}
        <div className="text-[10px] text-zinc-500 mt-2 font-mono">{(item.size_bytes / 1024).toFixed(0)} KB</div>
      </div>
      <button
        onClick={() => onDelete(item)}
        data-testid={`media-delete-${item.media_id}`}
        className="border-t border-zinc-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 flex items-center justify-center gap-1"
      >
        <Trash2 className="w-3.5 h-3.5" /> Delete
      </button>
    </div>
  );
}

function UploadDialog({ onClose, onUploaded }) {
  const [kind, setKind] = useState("photo");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a file first"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Max ${MAX_MB} MB`); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("title", title);
      fd.append("description", description);
      const { data } = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      onUploaded(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="media-upload-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl tracking-tight">Upload media</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">Kind</label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {KINDS.map((k) => {
              const Icon = k.icon;
              const sel = kind === k.value;
              return (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  data-testid={`upload-kind-${k.value}`}
                  className={`p-3 border text-xs font-semibold flex flex-col items-center gap-1 ${sel ? "bg-[#C8102E] text-white border-[#C8102E]" : "border-zinc-300 hover:bg-zinc-100"}`}
                >
                  <Icon className="w-5 h-5" />
                  {k.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">File</label>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT_BY_KIND[kind]}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="upload-file-input"
            className="w-full mt-1 text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#C8102E] file:text-white file:font-semibold file:cursor-pointer cursor-pointer"
          />
          <p className="text-[10px] text-zinc-500 mt-1">Max {MAX_MB} MB · {ACCEPT_BY_KIND[kind]}</p>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">Title (optional)</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="upload-title"
            placeholder="e.g. INOCHI cleanser hero shot"
            className="w-full mt-1 px-3 py-2 border border-zinc-300 text-sm outline-none focus:border-[#C8102E]"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest font-semibold text-zinc-600">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="upload-description"
            rows={2}
            className="w-full mt-1 px-3 py-2 border border-zinc-300 text-sm outline-none focus:border-[#C8102E] resize-none"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100">Cancel</button>
          <button
            type="submit"
            disabled={busy || !file}
            data-testid="upload-submit"
            className="px-4 py-2 text-sm font-semibold bg-[#C8102E] text-white hover:bg-[#9A0C24] disabled:opacity-50"
          >
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function MediaManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/media/mine", { params: filter ? { kind: filter } : {} });
      setItems(data);
    } catch (err) {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const onUploaded = (newItem) => {
    setItems((curr) => [newItem, ...curr]);
    setDialog(false);
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title || item.filename}"?`)) return;
    try {
      await api.delete(`/media/${item.media_id}`);
      setItems((curr) => curr.filter((m) => m.media_id !== item.media_id));
      toast.success("Deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <section className="border border-zinc-200 bg-white" data-testid="media-manager">
      <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">My media</div>
          <h2 className="font-display font-black text-xl tracking-tight">Photos, videos & marketing materials</h2>
          <p className="text-xs text-zinc-600 mt-1">Manage everything that appears on your showcase page. No admin needed.</p>
        </div>
        <button
          onClick={() => setDialog(true)}
          data-testid="media-upload-btn"
          className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2.5 font-semibold text-sm"
        >
          <Upload className="w-4 h-4" /> Upload
        </button>
      </div>

      <div className="px-4 py-3 border-b border-zinc-200 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("")}
          data-testid="media-filter-all"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${!filter ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"}`}
        >All ({items.length})</button>
        {KINDS.map((k) => (
          <button
            key={k.value}
            onClick={() => setFilter(k.value)}
            data-testid={`media-filter-${k.value}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${filter === k.value ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"}`}
          >{k.label}</button>
        ))}
      </div>

      <div className="p-4">
        {loading && <div className="text-center text-zinc-500 py-12 text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center text-zinc-500 py-12">
            <div className="text-sm">No media yet.</div>
            <button
              onClick={() => setDialog(true)}
              className="mt-3 inline-flex items-center gap-2 text-[#C8102E] hover:underline font-semibold text-sm"
            >
              <Upload className="w-4 h-4" /> Upload your first file
            </button>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="media-grid">
            {items.map((it) => <MediaCard key={it.media_id} item={it} onDelete={onDelete} />)}
          </div>
        )}
      </div>

      {dialog && <UploadDialog onClose={() => setDialog(false)} onUploaded={onUploaded} />}
    </section>
  );
}
